/**
 * Styles
 */
import './style.scss';

/**
 * External dependencies
 */
import clsx from 'clsx';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { render, useEffect, useRef } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';

/**
 * Internal dependencies
 */
import './store/admin';
import './store/settings';

import pageConfigs, { getDefaultPage } from './pages';

/**
 * Main admin page wrapper component
 *
 * Handles page routing and navigation
 *
 * @return {JSX.Element} The PageWrapper component
 */
const PageWrapper =() =>{
	const transitionRef = useRef();
	const { setActivePage } = useDispatch('marqueex/admin');

	const { activePage } = useSelect((select) => {
		const { getActivePage } = select('marqueex/admin');

		return {
			activePage: getActivePage(),
		};
	});

	// Highlight active links and change browser history.
	useEffect(() => {
		// disable active links.
		document
			.querySelectorAll('.toplevel_page_marqueex .current')
			.forEach(($el) => {
				$el.classList.remove('current');
			});

		// find new active link.
		let $links = document.querySelectorAll(
			`.toplevel_page_marqueex [href="admin.php?page=marqueex&sub_page=${activePage}"]`
		);

		if (!$links || !$links.length) {
			$links = document.querySelectorAll(
				'.toplevel_page_marqueex [href="admin.php?page=marqueex"]'
			);
		}

		$links.forEach(($link) => {
			$link.parentNode.classList.add('current');
		});

		// Change body class.
		document.body.classList.forEach((className) => {
			if (/marqueex-admin-page-/.test(className)) {
				document.body.classList.remove(className);
			}
		});
		document.body.classList.add(`marqueex-admin-page-${activePage}`);

		// change address bar link
		if ($links && $links.length) {
			window.history.pushState(
				document.title,
				document.title,
				$links[0].href
			);
		}
	}, [activePage]);

	const resultNavItems = [];
	let resultContent = '';

	// Generate navigation items from page configurations
	Object.entries(pageConfigs).forEach(([pageKey, pageConfig]) => {
		resultNavItems.push(
			<a
				key={pageKey}
				href={(pageConfig.href) || '#'}
				target={pageConfig.href ? '_blank' : null}
				rel="noreferrer"
				className={clsx(
					activePage === pageKey && 'active'
				)}
				onClick={(e) => {
					if (!pageConfig.href) {
						e.preventDefault();
						setActivePage(pageKey);
					}
				}}
			>
				{pageConfig.label}
			</a>
		);
	});

	// Render active page content
	if (activePage && pageConfigs[activePage]) {
		const PageComponent = pageConfigs[activePage].block;
		resultContent = <PageComponent />;
	}

	// Get plugin version from global variable if available
	const pluginVersion = window.marqueexAdminData?.version || '0.0.6';

	const activePageConfig = pageConfigs[activePage] || {};

	return (
    <>
      <div className="marqueex-admin-header">
        <div className="marqueex-admin-header-bg"></div>
        <div className="marqueex-admin-content header-content">
          <div className="marqueex-admin-header-wrap">
            <div className="marqueex-admin-header-logo">
              <div className="logo-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  id="Maya-Logo--Streamline-Logos"
                  height="24"
                  width="24"
                >
                  <path
                    fill="#fff"
                    fill-rule="evenodd"
                    d="M1 1.948 7 1.5l4.994 10.987L13.988 18H10.5l-4.727 -6.913 -4.772 -9.139Zm0 3.24v14.71c1.574 -2.424 2.857 -4.752 3.76 -7.511L1 5.187ZM1.064 22.5c2.955 -0.003 4.125 -0.214 4.65 -1v-7.325c-1.169 3.028 -2.779 5.59 -4.65 8.325Zm13.905 -6.197 2.77 -4.847L22.967 2H16.5l-3.636 8.483 2.105 5.82ZM23 5.04l-5 9.046v5.468a35.72 35.72 0 0 0 2.834 -5.037C21.96 12.081 22.768 9.61 23 7.584V5.039Zm0 8.227a35.322 35.322 0 0 1 -0.804 1.879c-1.174 2.542 -2.66 5.01 -4.15 6.878L19 22.5h4v-9.234Z"
                    clip-rule="evenodd"
                    stroke-width="1"
                  ></path>
                </svg>
              </div>
              <h1>
                {__("MarqueeX", "marqueex")}
                <span>v{pluginVersion}</span>
              </h1>
            </div>
            <nav className="marqueex-admin-header-nav">{resultNavItems}</nav>
          </div>
        </div>
      </div>

      <SwitchTransition mode="out-in">
        <CSSTransition
          key={activePage}
          nodeRef={transitionRef}
          timeout={200}
          classNames="fade"
          unmountOnExit
        >
          <div
            ref={transitionRef}
            className={clsx(
              "marqueex-admin-content",
              `marqueex-admin-content-${activePage}`
            )}
          >
            <div className="marqueex-admin-content-card">{resultContent}</div>
          </div>
        </CSSTransition>
      </SwitchTransition>

      <footer className="marqueex-admin-footer">
        <p>
          {__("MarqueeX", "marqueex")} v{pluginVersion} |{" "}
          <a
            href="https://wpxero.com/marqueex"
            target="_blank"
            rel="noopener noreferrer"
          >
            {__("Documentation", "marqueex")}
          </a>{" "}
          |{" "}
          <a
            href="https://wordpress.org/support/plugin/marqueex/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {__("Support", "marqueex")}
          </a>
        </p>
      </footer>
    </>
  );
}


export default PageWrapper;
